import Image from 'next/image'
import { FunctionComponent, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import arrowIconUrl from '@/assets/images/pptx/card-icon/arrow.png'
import eyeIconUrl from '@/assets/images/pptx/card-icon/eye.png'
import likeIconUrl from '@/assets/images/pptx/card-icon/like.png'
import messageIconUrl from '@/assets/images/pptx/card-icon/message.png'
import playIconUrl from '@/assets/images/pptx/card-icon/play.png'
import type { OurIntlShape } from '@/i18n/typing'
import PlatformImage from '@/utils/export-pptx/components/platform-image'
import {
  SlideContainer,
  SlideTitle,
} from '@/utils/export-pptx/components/slides/styles'
import { PptxObjectType } from '@/utils/export-pptx/constants'
import useImageSizes, {
  BASE_IMAGE_WIDTH,
} from '@/utils/export-pptx/hooks/use-image-sizes'
import sliceTextByDisplayLength from '@/utils/export-pptx/slice-text-by-display-length'
import type { KolPostWallSlide } from '@/utils/export-pptx/types/slide'
import { getThumbnail } from '@/utils/get-thumbnails'

const MAX_POST_TITLE_LENGTH = 60

interface SlideProps {
  slide: KolPostWallSlide
  onSlideReady?: (slide: KolPostWallSlide) => void
  formatMessage: OurIntlShape['formatMessage']
}

const SlideComponent: FunctionComponent<SlideProps> = ({
  slide,
  onSlideReady,
  formatMessage,
}) => {
  const thumbnailUrls = useMemo(
    () => slide.payload.posts.map((item) => item.thumbnail),
    [slide.payload.posts],
  )

  const images = useImageSizes(thumbnailUrls, BASE_IMAGE_WIDTH)

  useEffect(() => {
    if (images === undefined) {
      return
    }

    onSlideReady?.(slide)
  }, [onSlideReady, slide, images])

  return (
    <SlideContainer data-pptx-object={PptxObjectType.Shape}>
      <SlideTitle data-pptx-object={PptxObjectType.Shape}>
        <span data-pptx-object={PptxObjectType.Text}>
          {formatMessage({ id: 'general:pptx_title_kol_info' })}
        </span>
      </SlideTitle>

      <SlideContent>
        <CardContentItem>
          <Image
            priority
            unoptimized
            alt='kol thumbnail'
            data-pptx-object={PptxObjectType.Image}
            height={180}
            src={getThumbnail(slide.payload.kol.uuid)}
            style={{ borderRadius: '50%' }}
            width={180}
          />

          <KolName data-pptx-object={PptxObjectType.Text}>
            {slide.payload.kol.name}
          </KolName>
        </CardContentItem>

        {slide.payload.posts.map((post, index) => (
          <CardContentItem key={post.id}>
            <PostCard>
              {images?.[index] && (
                <Image
                  priority
                  unoptimized
                  alt='post'
                  data-pptx-object={PptxObjectType.Image}
                  height={images[index].height}
                  key={images[index].url}
                  src={images[index].url}
                  width={images[index].width}
                />
              )}

              <PostCardContent data-pptx-object={PptxObjectType.Shape}>
                {post.platform !== 'custom' && (
                  <PlatformWrapper>
                    <PlatformImage
                      height={16}
                      platform={post.platform}
                      width={16}
                    />
                    <span data-pptx-object={PptxObjectType.Text}>
                      {post.publishedAt}
                    </span>
                  </PlatformWrapper>
                )}

                {post.caption && (
                  <Caption data-pptx-object={PptxObjectType.Text}>
                    {sliceTextByDisplayLength(
                      post.caption,
                      MAX_POST_TITLE_LENGTH,
                    )}
                  </Caption>
                )}

                <AdditationalInfo>
                  {post.data.likeCount && (
                    <Statistic>
                      <Image
                        priority
                        unoptimized
                        alt='like'
                        data-pptx-object={PptxObjectType.Image}
                        height={16}
                        src={likeIconUrl}
                        width={16}
                      />
                      <span data-pptx-object={PptxObjectType.Text}>
                        {post.data.likeCount}
                      </span>
                    </Statistic>
                  )}

                  {post.data.commentCount && (
                    <Statistic>
                      <Image
                        priority
                        unoptimized
                        alt='message'
                        data-pptx-object={PptxObjectType.Image}
                        height={16}
                        src={messageIconUrl}
                        width={16}
                      />
                      <span data-pptx-object={PptxObjectType.Text}>
                        {post.data.commentCount}
                      </span>
                    </Statistic>
                  )}

                  {post.data.viewCount && (
                    <Statistic>
                      <Image
                        priority
                        unoptimized
                        alt='eye'
                        data-pptx-object={PptxObjectType.Image}
                        height={16}
                        src={eyeIconUrl}
                        width={16}
                      />
                      <span data-pptx-object={PptxObjectType.Text}>
                        {post.data.viewCount}
                      </span>
                    </Statistic>
                  )}

                  {post.data.playCount && (
                    <Statistic>
                      <Image
                        priority
                        unoptimized
                        alt='play'
                        data-pptx-object={PptxObjectType.Image}
                        height={16}
                        src={playIconUrl}
                        width={16}
                      />
                      <span data-pptx-object={PptxObjectType.Text}>
                        {post.data.playCount}
                      </span>
                    </Statistic>
                  )}

                  {post.data.shareCount && (
                    <Statistic data-pptx-object={PptxObjectType.Text}>
                      <Image
                        priority
                        unoptimized
                        alt='arrow'
                        data-pptx-object={PptxObjectType.Image}
                        height={16}
                        src={arrowIconUrl}
                        width={16}
                      />
                      <span data-pptx-object={PptxObjectType.Text}>
                        {post.data.shareCount}
                      </span>
                    </Statistic>
                  )}
                </AdditationalInfo>
              </PostCardContent>
            </PostCard>
          </CardContentItem>
        ))}
      </SlideContent>
    </SlideContainer>
  )
}

const SlideContent = styled.div`
  display: flex;
  justify-content: space-around;
  gap: 60px;

  height: 600px;
`

const CardContentItem = styled.div`
  position: relative;
  flex: 1;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const KolName = styled.p`
  margin-top: 16px;

  font-size: 24px;
  font-weight: 700;
`

const PostCard = styled.div`
  width: ${BASE_IMAGE_WIDTH}px;

  border-radius: 6px;
  overflow: hidden;
`

const PostCardContent = styled.div`
  display: grid;
  gap: 8px;
  padding: 12px 16px;

  background-color: ${({ theme }): string => theme.colors.background.white};
`

const PlatformWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${({ theme }): string => theme.colors.base.grey.grey3};
`

const Caption = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;

  font-size: 14px;
  font-weight: 700;
  line-height: 22.4px;
  color: ${({ theme }): string => theme.colors.text.primary};
`

const AdditationalInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const Statistic = styled.div`
  font-size: 14px;
  color: ${({ theme }): string => theme.colors.text.tooltip};
  fill: ${({ theme }): string =>
    theme.colors.text.tooltip}; /* shared/icon 有些圖案不吃 color */
  display: flex;
  align-items: center;
  gap: 4px;
`

export default SlideComponent
